import { useEffect, useState } from 'preact/hooks';
import { Home } from './pages/Home';
import { Engine } from './pages/Engine';
import { Garage } from './pages/Garage';
import { Diag } from './pages/Diag';
import { Unlock } from './pages/Unlock';
import { Bridge } from './pages/Bridge';
import { Admin } from './pages/Admin';
import { Link } from './pages/Link';
import { Legal } from './pages/Legal';
import { isLegalDocumentId, type LegalDocumentId } from './legalContent';
import { CARS, getCar, carPath } from '../cars/registry';
import { isAdmin, isCarUnlocked, syncEntitlements } from '../utils/access';
import { useRelayControl } from '../utils/relayControl';
import { prefetchOwned } from '../utils/offline';

type Route = { page: 'home'; account?: boolean } | { page: 'engine'; carId: string } | { page: 'garage'; carId: string } | { page: 'unlock'; carId: string } | { page: 'diag' } | { page: 'admin' } | { page: 'bridge'; room: string } | { page: 'link'; code: string } | { page: 'legal'; doc: LegalDocumentId };

function routeOf(h: string): Route {
  if (h === 'diag') return { page: 'diag' };
  const mlg = /^legal\/([a-z-]+)$/.exec(h);
  if (mlg && isLegalDocumentId(mlg[1])) return { page: 'legal', doc: mlg[1] };
  if (h === 'legal') return { page: 'legal', doc: 'terms' };
  if (h === 'admin') return { page: 'admin' };
  if (h === 'account') return { page: 'home', account: true };
  if (h === 'input') return { page: 'home' }; // legacy deep link — input setup now lives in each car's garage bay
  const ml = /^link\/([A-Za-z0-9_-]{1,64})$/.exec(h);
  if (ml) return { page: 'link', code: ml[1] };
  const mb = /^bridge\/([A-Za-z0-9_-]{1,64})$/.exec(h);
  if (mb) return { page: 'bridge', room: mb[1] };
  const mc = /^car\/([a-z0-9-]+)$/.exec(h);
  // resolve the pretty slug back to the internal id here, once: everything downstream (purchases, grants,
  // demo budgets, audio paths) is keyed by the id and must never see the slug
  if (mc && getCar(mc[1])?.available) return { page: 'engine', carId: getCar(mc[1])!.id };
  const mg = /^garage\/([a-z0-9-]+)$/.exec(h);
  if (mg && getCar(mg[1])?.available) return { page: 'garage', carId: getCar(mg[1])!.id };
  const mu = /^unlock\/([a-z0-9-]+)$/.exec(h);
  if (mu && getCar(mu[1])?.available) return { page: 'unlock', carId: getCar(mu[1])!.id };
  return { page: 'home' };
}

/** CLEAN paths (Marc: no '#' in the address bar) — roardrive.com/garage/x, /car/x, /legal/terms… */
function parseRoute(): Route {
  // legacy #hash links (old bookmarks, the app's remote control of older screens) → clean path once
  const h = location.hash.replace(/^#\/?/, '').split('?')[0];
  if (h && h !== 'can-bridge') {
    history.replaceState(null, '', '/' + h + location.search);
  }
  const p = location.pathname.replace(/^\/+/, '').replace(/\/+$/, '');
  const route = routeOf(p);
  // the address bar shows the car's NAME, never its internal id (Marc saw /garage/porsche-911-rsr-copy
  // again): an old bookmark, the app's deep link or any link built from the id is rewritten once, here
  if ('carId' in route) {
    const car = getCar(route.carId);
    const pretty = car ? `/${route.page === 'engine' ? 'car' : route.page}/${carPath(car)}` : null;
    if (pretty && location.pathname !== pretty) history.replaceState(null, '', pretty + location.search);
  }
  return route;
}

export function navigate(to: string): void {
  const path = to.startsWith('/') ? to : '/' + to.replace(/^#\/?/, '');
  history.pushState(null, '', path);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

export function App() {
  const [route, setRoute] = useState<Route>(parseRoute);
  useRelayControl(); // phone app → pick car / home / source
  useEffect(() => {
    // purchases recorded in Supabase follow the device; and once they are known, the engines they bought go
    // on the device too, so the next tunnel changes nothing (utils/offline.ts)
    void syncEntitlements().then(() => prefetchOwned());
    const onNav = () => setRoute(parseRoute());
    window.addEventListener('popstate', onNav);
    window.addEventListener('hashchange', onNav); // legacy #links landing while running
    // internal anchors (href="/garage/x", "/legal/…") stay in the SPA — no full page reload
    const onClick = (e: MouseEvent) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const a = (e.target as Element | null)?.closest?.('a');
      if (!a) return;
      const href = a.getAttribute('href') || '';
      if (a.target === '_blank' || a.hasAttribute('download')) return;
      if (!href.startsWith('/') || href.startsWith('//')) return;
      if (/^\/(app|audio|cars\.json|logo|brand)/.test(href)) return; // real files, not routes
      e.preventDefault();
      navigate(href);
    };
    document.addEventListener('click', onClick);
    return () => { window.removeEventListener('popstate', onNav); window.removeEventListener('hashchange', onNav); document.removeEventListener('click', onClick); };
  }, []);

  if (route.page === 'bridge') return <Bridge room={route.room} />; // public setup page (laptop)
  if (route.page === 'link') return <Link code={route.code} />; // phone: sign in for the Tesla that showed the QR
  if (route.page === 'legal') return <Legal id={route.doc} />;

  switch (route.page) {
    case 'engine':
      // every available car is enterable: unpurchased cars run in DEMO mode
      // (capped at 50 km/h with the paywall popup inside the Engine page)
      return <Engine profile={getCar(route.carId)!} />;
    case 'garage':
      // the pre-drive "room": car → garage bay → dash; the dash exits back into the bay
      return <Garage profile={getCar(route.carId)!} cars={CARS} />;
    case 'unlock': {
      if (isCarUnlocked(route.carId)) {
        navigate(`/car/${carPath(getCar(route.carId)!)}`);
        return <Engine profile={getCar(route.carId)!} />;
      }
      return <Unlock profile={getCar(route.carId)!} />;
    }
    case 'diag':
      return <Diag />;
    case 'admin':
      return isAdmin() ? <Admin /> : <Home cars={CARS} />;
    default:
      return <Home cars={CARS} openAccount={route.page === 'home' && !!route.account} />;
  }
}
